import { config } from '@/config'
import type { NurtureType } from '@/machines/creatureCareMachine'
import type { Biome } from '@/types/common'

export const kidFirstShellEnabled = config.features.kidFirstShell

export const BIOME_LABELS: Record<Biome, string> = {
  Urban: 'City',
  Temperate: 'Forest',
  Tropical: 'Jungle',
  Desert: 'Desert',
  Mountain: 'Mountain',
  Wetland: 'Swamp',
  Coastal: 'Beach',
  Arctic: 'Snow',
}

export const BIOME_VIBES: Record<Biome, string> = {
  Urban: 'City spark',
  Temperate: 'Forest trail',
  Tropical: 'Jungle dash',
  Desert: 'Desert glow',
  Mountain: 'Mountain climb',
  Wetland: 'Swamp splash',
  Coastal: 'Beach breeze',
  Arctic: 'Snow drift',
}

export const kidFirstNurtureActions: Record<
  NurtureType,
  {
    label: string
    cheer: string
    detail: string
    result: string
    confirmLabel: string
    immersiveLabel: string
    statHint: string
  }
> = {
  feed: {
    label: 'Feed',
    cheer: 'Yum!',
    detail: 'A quick snack and a little growth.',
    result: 'looks happy and full.',
    confirmLabel: 'Feed now',
    immersiveLabel: 'Feed and Return',
    statHint: 'Snack boost',
  },
  play: {
    label: 'Play',
    cheer: 'Zoom!',
    detail: 'A happy run for faster moves.',
    result: 'is buzzing with extra speed.',
    confirmLabel: 'Play now',
    immersiveLabel: 'Play and Return',
    statHint: 'Speed boost',
  },
  train: {
    label: 'Train',
    cheer: 'Power up!',
    detail: 'Practice hard for a bigger move.',
    result: 'looks stronger after practice.',
    confirmLabel: 'Train now',
    immersiveLabel: 'Train and Return',
    statHint: 'Power boost',
  },
  rest: {
    label: 'Rest',
    cheer: 'Nap time!',
    detail: 'A cozy break to feel safe and steady.',
    result: 'looks calm and ready again.',
    confirmLabel: 'Rest now',
    immersiveLabel: 'Rest and Return',
    statHint: 'Calm boost',
  },
}

export const kidFirstCopy = {
  grownUpsLabel: 'Grown-Ups',
  login: {
    title: 'WEFA',
    subtitle: 'Grow a creature. Snap a plant. Play a round.',
    primaryAction: 'Start Playing',
    quickStartHelp: 'Saves your game on this device.',
    fallbackHelp: 'Ask a grown-up to help you sign in.',
    advancedToggleClosed: 'Grown-Ups: more ways to sign in',
    advancedToggleOpen: 'Hide grown-up sign-in',
    familyOptionsLabel: 'Email, Apple, or Google',
    walletLabel: 'Connect Wallet',
    blueskyPlaceholder: 'yourname.bsky.social',
    blueskyLabel: 'Bluesky',
    footer: 'Your garden is ready when you are.',
  },
  onboarding: {
    title: "Let's hatch your first creature",
    copy: 'Pick an element, choose a place, hatch an egg, and name your buddy.',
    elementTitle: '1. Pick your element',
    locationTitle: '2. Pick your home place',
    locationLabel: 'What should we call your place?',
    locationPlaceholder: 'Backyard garden',
    biomeLabel: 'What kind of place is it?',
    locationContinue: 'Next',
    creatureTitle: '3. Choose your creature egg',
    creatureCopy: 'Each egg has its own move, mood, and style.',
    creatureSelect: 'Pick this egg',
    completeTitle: '4. Name your creature',
    namePlaceholder: 'Type a creature name',
    finish: "Let's Go!",
    creating: 'Saving your buddy...',
  },
  nextStep: {
    nurtureMessage: 'Your creature wants a snack.',
    nurtureAction: 'Feed',
    evolveMessage: 'Your creature is ready to grow.',
    evolveAction: 'Grow',
    playMessage: 'Your creature wants to play.',
    playAction: 'Play',
  },
  garden: {
    welcomeTitle: 'Snap your first plant.',
    welcomeCopy: 'Plant care helps your creature grow.',
    welcomeAction: "Let's go",
    snapLabel: 'Snap',
    feedLabel: 'Feed',
    growLabel: 'Grow',
    playLabel: 'Play',
    cameraStreaming: 'Find green leaves. Tap Snap.',
    cameraCaptured: 'Nice catch! Keep this one or try again.',
    cameraStarting: 'Waking up the camera...',
    firstPlantTitle: 'Nice catch!',
    firstPlantCopy: 'Give this plant friend a name.',
    careTitle: 'Feed your buddy',
    careCopy: 'One check-in. One snack.',
    careAction: 'Feed my creature',
    addPlantTitle: 'Add a new plant friend',
    addPlantAction: 'Add this plant',
    plantPlaceholder: 'Plant name',
  },
  nurture: {
    title: 'Pick what your creature wants',
    copy: 'One quick tap helps your buddy right now.',
    lowEnergy: 'Pick a smaller action or come back after a game.',
    backAction: 'Back to Camera',
  },
  gameSetup: {
    chooseGameTitle: 'Pick a game',
    chooseCreaturesTitle: 'Pick your creatures',
    readyTitle: 'Game time!',
    quickStartTitle: 'Quick Match',
    quickStartCopy: 'Same device. Creatures ready. Start fast.',
    quickStartAction: 'Quick Match',
    chooseCreaturesAction: 'Pick creatures myself',
    advancedToggleClosed: 'Play on another device',
    advancedToggleOpen: 'Hide other device options',
    yourCreatureLabel: 'You, pick your creature',
    friendCreatureLabel: 'Friend, pick your creature',
    readyAction: 'Start game',
    oneRuleLabel: 'One rule to remember',
    moreRulesAction: 'Show full rules',
    fewerRulesAction: 'Hide extra rules',
  },
  deck: {
    plantsTitle: 'Plant Friends',
    plantsCopy: 'Tap a plant friend to see how it feels today.',
    creaturesTitle: 'Creature Buddies',
    creaturesCopy: 'Tap a buddy to check moves, vibes, and growth.',
    attentionTitle: 'Help Next',
    stableTitle: 'Doing Great',
    emptyPlants: 'No plant friends yet. Snap one in Garden.',
    emptyPlantsAction: 'Go to Garden',
    emptyCreatures: 'No creature buddy yet. Hatch one first.',
    emptyCreaturesAction: 'Hatch My Creature',
    filterAction: 'Sort and filter',
  },
  filters: {
    plantTitle: 'Pick plant friends',
    creatureTitle: 'Pick creature buddies',
    clearAction: 'Clear picks',
    elementTitle: 'Pick an element',
    healthTitle: 'How it feels',
    plantGrowthTitle: 'Growth stage',
    creatureGrowthTitle: 'Grow stage',
  },
  profile: {
    heroCopy: 'Your garden crew lives here.',
    badgesCopy: 'Kind care and big wins show up here.',
    lookTitle: 'Look',
    lookCopy: 'Pick the colors that feel best for your garden.',
    grownUpToggleClosed: 'Grown-up stuff',
    grownUpToggleOpen: 'Hide grown-up stuff',
    linkedTitle: 'Linked Identities',
    linkedCopy: 'These stay tucked away unless a grown-up needs them.',
    creatorTitle: 'UGC Studio',
    creatorCopy: 'Grown-up maker tools for packs, skins, and remix experiments.',
  },
} as const
